/**
 * Live robot position on the map.
 *
 * Peribott Dynamic LLP — Author: Amit Jagnani
 *
 * v6.3.39. Subscribes to the viewer-gated `pose_update` channel: the robot
 * only publishes while this component is mounted, so an unwatched fleet costs
 * nothing. Frames arrive at ~2 Hz and are interpolated client-side, which
 * looks smooth without paying for a 10 Hz stream or writing anything to the
 * telemetry collection.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Card, CardContent, Typography, Chip, Alert, Tooltip } from '@mui/material';
import { addEventListener, removeEventListener, startPoseStream, stopPoseStream } from '../../utils/socketClient';
import apiClient from '../../utils/apiClient';

type Pose = { x: number; y: number; theta: number; stale?: boolean };

interface Props {
  robotSerial: string;
  mapId?: string | null;
  /** Fallback pose (e.g. from telemetry) shown before the first stream frame */
  initialPose?: Pose | null;
  /** AMCL/map_manager localization state. When false the robot's pose is
   *  meaningless (it reports 0,0), so we must NOT draw a marker — placing a
   *  robot at the map origin is worse than showing nothing. */
  localized?: boolean;
  height?: number;
}

const LiveMap: React.FC<Props> = ({ robotSerial, mapId, initialPose, localized = true, height = 460 }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  const [mapMeta, setMapMeta] = useState<any>(null);
  const [imgSize, setImgSize] = useState({ width: 0, height: 0 });
  const [error, setError] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const [lastRx, setLastRx] = useState<number | null>(null);

  // Target = newest pose from the wire. Render = what's drawn, eased toward
  // target each frame so 2 Hz updates don't look like teleporting.
  const target = useRef<Pose | null>(initialPose || null);
  const render = useRef<Pose | null>(initialPose || null);
  // v6.3.47: the canvas is drawn imperatively, so the draw loop must NOT
  // trigger React renders. It previously called a forceTick setState on every
  // animation frame — 60 full component re-renders per second for a readout
  // that only needs to change a couple of times a second.
  const [poseText, setPoseText] = useState<Pose | null>(initialPose || null);

  // ---- map metadata + image -------------------------------------------
  useEffect(() => {
    let cancelled = false;
    if (!mapId) { setMapMeta(null); return; }
    (async () => {
      try {
        const r = await apiClient.get(`/maps/${mapId}`);
        if (!cancelled) setMapMeta(r.data);
      } catch {
        if (!cancelled) setError('Could not load map metadata');
      }
    })();
    return () => { cancelled = true; };
  }, [mapId]);

  useEffect(() => {
    if (!mapId) return;
    const img = new Image();
    img.onload = () => { imgRef.current = img; setImgSize({ width: img.width, height: img.height }); };
    img.onerror = () => setError('Could not load map image');
    // apiClient carries the auth header; the <img> tag cannot, so fetch as a blob.
    apiClient.get(`/maps/${mapId}/image`, { responseType: 'blob' })
      .then((res) => { img.src = URL.createObjectURL(res.data); })
      .catch(() => setError('Could not load map image'));
    return () => { if (img.src.startsWith('blob:')) URL.revokeObjectURL(img.src); };
  }, [mapId]);

  // ---- world <-> pixel (same convention as MapEditor) ------------------
  const origin = useMemo(() => {
    const o = mapMeta?.origin;
    if (!o) return { x: 0, y: 0 };
    if (Array.isArray(o)) return { x: o[0] || 0, y: o[1] || 0 };
    return { x: o.x || 0, y: o.y || 0 };
  }, [mapMeta]);
  const resolution = mapMeta?.resolution || 0.05;

  const worldToPixel = useCallback((wx: number, wy: number) => ({
    x: (wx - origin.x) / resolution,
    y: imgSize.height - 1 - (wy - origin.y) / resolution,
  }), [origin, resolution, imgSize.height]);

  // ---- subscribe to the pose stream -----------------------------------
  useEffect(() => {
    if (!robotSerial) return;
    const onPose = (d: any) => {
      if (d?.robot_serial !== robotSerial) return;
      if (typeof d.x !== 'number' || typeof d.y !== 'number') return;
      target.current = { x: d.x, y: d.y, theta: d.theta || 0, stale: !!d.stale };
      if (render.current == null) render.current = target.current;
      if (!d.stale) { setConnected(true); setLastRx(Date.now()); }
    };
    addEventListener('pose_update', onPose);
    startPoseStream(robotSerial);
    return () => {
      stopPoseStream(robotSerial);
      removeEventListener('pose_update', onPose);
    };
  }, [robotSerial]);

  // v6.3.49 BUGFIX: keep the marker fed from telemetry.
  //
  // target/render/poseText were seeded with `initialPose` via useRef/useState,
  // which only captures the value present at the FIRST render. Telemetry
  // arrives asynchronously a moment later, so the prop went null -> real pose
  // while the refs stayed null forever: no marker was ever drawn and the
  // coordinate readout stayed blank, even for a localized, moving robot.
  //
  // Telemetry already carries pose (every ~5s), so this also means the map
  // works as a slow-updating fallback when the 2 Hz stream is unavailable —
  // e.g. before the robot-side pose patch is deployed.
  useEffect(() => {
    if (!initialPose) return;
    if (typeof initialPose.x !== 'number' || typeof initialPose.y !== 'number') return;
    // Never let the slower telemetry pose fight a live stream frame.
    if (connected) return;
    target.current = {
      x: initialPose.x,
      y: initialPose.y,
      theta: initialPose.theta || 0,
    };
    if (!render.current) render.current = { ...target.current };
    setPoseText({ ...target.current });
  }, [initialPose, connected]);

  // Mark the feed stale if frames stop (robot offline / stream ended), and
  // refresh the numeric readout at a human-readable rate.
  useEffect(() => {
    const t = setInterval(() => {
      if (lastRx && Date.now() - lastRx > 5000) setConnected(false);
      setPoseText(target.current ? { ...target.current } : null);
    }, 500);
    return () => clearInterval(t);
  }, [lastRx]);

  // ---- draw loop -------------------------------------------------------
  useEffect(() => {
    let raf = 0;
    const draw = () => {
      raf = requestAnimationFrame(draw);
      const canvas = canvasRef.current;
      const wrap = wrapRef.current;
      if (!canvas || !wrap) return;

      const cw = wrap.clientWidth;
      const ch = height;
      if (canvas.width !== cw || canvas.height !== ch) { canvas.width = cw; canvas.height = ch; }
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.clearRect(0, 0, cw, ch);

      const img = imgRef.current;
      let scale = 1, offX = 0, offY = 0;
      if (img && imgSize.width) {
        scale = Math.min(cw / imgSize.width, ch / imgSize.height);
        offX = (cw - imgSize.width * scale) / 2;
        offY = (ch - imgSize.height * scale) / 2;
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(img, offX, offY, imgSize.width * scale, imgSize.height * scale);
      } else {
        ctx.fillStyle = 'rgba(128,128,128,0.08)';
        ctx.fillRect(0, 0, cw, ch);
      }

      // Ease toward the newest pose (~simple exponential smoothing).
      const tgt = target.current;
      if (tgt) {
        if (!render.current) render.current = { ...tgt };
        const r = render.current;
        const a = 0.18;
        r.x += (tgt.x - r.x) * a;
        r.y += (tgt.y - r.y) * a;
        // shortest-arc angle interpolation
        let dth = tgt.theta - r.theta;
        while (dth > Math.PI) dth -= 2 * Math.PI;
        while (dth < -Math.PI) dth += 2 * Math.PI;
        r.theta += dth * a;

        // v6.3.47: only draw the robot when its pose is actually meaningful.
        // An unlocalized robot reports (0,0), and drawing a marker at the map
        // origin tells the operator the robot is somewhere it is not.
        if (img && imgSize.width && localized) {
          const p = worldToPixel(r.x, r.y);
          const cx = offX + p.x * scale;
          const cy = offY + p.y * scale;

          // Pulsing halo — makes the robot findable on a large map at a glance.
          const pulse = 1 + 0.25 * Math.sin(Date.now() / 320);
          const size = Math.max(12, (0.35 / resolution) * scale);

          ctx.beginPath();
          ctx.arc(cx, cy, size * 2.1 * pulse, 0, Math.PI * 2);
          ctx.fillStyle = connected ? 'rgba(33,150,243,0.13)' : 'rgba(158,158,158,0.12)';
          ctx.fill();

          ctx.beginPath();
          ctx.arc(cx, cy, size * 1.5, 0, Math.PI * 2);
          ctx.strokeStyle = connected ? 'rgba(33,150,243,0.55)' : 'rgba(158,158,158,0.45)';
          ctx.lineWidth = 2;
          ctx.stroke();

          // Heading arrow. Canvas Y grows downward, world yaw is CCW -> negate.
          ctx.save();
          ctx.translate(cx, cy);
          ctx.rotate(-r.theta);
          ctx.fillStyle = connected ? '#2196f3' : '#9e9e9e';
          ctx.strokeStyle = '#fff';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(size * 1.25, 0);
          ctx.lineTo(-size * 0.7, size * 0.8);
          ctx.lineTo(-size * 0.28, 0);
          ctx.lineTo(-size * 0.7, -size * 0.8);
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
          ctx.restore();

          // If the robot sits outside the rendered map, say so rather than
          // drawing it silently off in the grey margin.
          const outside = p.x < 0 || p.y < 0 || p.x > imgSize.width || p.y > imgSize.height;
          if (outside) {
            ctx.fillStyle = '#d32f2f';
            ctx.font = '12px sans-serif';
            ctx.fillText('robot is outside this map', 10, 18);
          }
        }
      }
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [imgSize, worldToPixel, resolution, height, connected, localized]);

  const pose = poseText;

  return (
    <Card>
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <Typography variant="h6" fontWeight={600} sx={{ flex: 1, minWidth: 0 }}>Live Position</Typography>
          {/* v6.3.47: distinguish the two very different reasons for no dot.
              "No live feed" was shown even when the real cause was that the
              robot isn't localized — AMCL publishes no pose, so the robot
              sends no frames. That sent operators looking for a network fault
              that doesn't exist. */}
          {/* v6.3.49: "No live feed" was ambiguous — it looked like a fault
              even when the marker was tracking fine from telemetry. Three
              honest states: live 2 Hz stream, slower telemetry-only tracking,
              or genuinely nothing. */}
          <Tooltip title={
            !localized ? 'AMCL reports the robot is not localized'
              : connected ? 'Live pose stream (~2 Hz)'
              : pose ? 'Position from telemetry (updates every few seconds). '
                       + 'For smooth tracking, deploy the pose-stream patch on this robot.'
              : 'No position data received yet'
          }>
            <Chip
              size="small"
              label={!localized ? 'Awaiting localization'
                : connected ? 'Live'
                : pose ? 'Telemetry only'
                : 'No position data'}
              color={!localized ? 'warning' : connected ? 'success' : pose ? 'info' : 'default'}
              variant={connected && localized ? 'filled' : 'outlined'}
            />
          </Tooltip>
          {pose && localized && (
            <Typography variant="caption" color="text.secondary">
              x {pose.x.toFixed(2)} m · y {pose.y.toFixed(2)} m · θ {(pose.theta * 180 / Math.PI).toFixed(0)}°
            </Typography>
          )}
        </Box>
        {error && <Alert severity="warning" sx={{ mb: 1 }}>{error}</Alert>}
        {!mapId && (
          <Alert severity="info" sx={{ mb: 1 }}>
            No map selected — position is shown numerically only. Load a map on this
            robot to see it plotted.
          </Alert>
        )}
        {mapId && !localized && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            Robot is not localized, so it reports no position — the map below is
            shown for reference only. Localize the robot (set its initial pose)
            and the live marker will appear.
          </Alert>
        )}
      </CardContent>
      <Box ref={wrapRef} sx={{ width: '100%', px: 2, pb: 2 }}>
        <canvas ref={canvasRef} style={{ width: '100%', height, display: 'block', borderRadius: 8 }} />
      </Box>
    </Card>
  );
};

export default LiveMap;
